import React, { useState } from 'react';
import {
  Box,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Badge,
  Card,
  CardBody,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  FormControl,
  FormLabel,
  Textarea,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../services/api';
import { FiPlus, FiEdit, FiTrash2, FiTool } from 'react-icons/fi';
import { useLanguage } from '../contexts/LanguageContext';

const SettingsServiceCategories = () => {
  const { t, currentLanguage } = useLanguage();
  const toast = useToast();
  const queryClient = useQueryClient();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  // Service Categories state
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({
    name_de: '',
    name_en: '',
    description_de: '',
    description_en: '',
    color: '#3182CE'
  });

  // Service Categories queries and mutations
  const { data: categoriesData } = useQuery(
    ['service-categories'],
    async () => {
      const response = await api.get('/api/services/categories');
      return response.data;
    }
  );

  const createCategory = useMutation(
    async (categoryData) => {
      const response = await api.post('/api/services/categories', categoryData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['service-categories']);
        toast({ title: t('common.success'), description: t('services.category_created_success', {defaultValue: 'Category created successfully'}), status: 'success' });
        setShowCategoryModal(false);
        setCategoryFormData({
          name_de: '', name_en: '', description_de: '', description_en: '',
          color: '#3182CE'
        });
      },
      onError: (error) => {
        toast({ title: t('common.error'), description: error.response?.data?.error || t('services.category_create_failed', {defaultValue: 'Failed to create category'}), status: 'error' });
      }
    }
  );

  const updateCategory = useMutation(
    async ({ id, data }) => {
      const response = await api.put(`/api/services/categories/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['service-categories']);
        toast({ title: t('common.success'), description: t('services.category_updated_success', {defaultValue: 'Category updated successfully'}), status: 'success' });
        setShowCategoryModal(false);
        setEditingCategory(null);
        setCategoryFormData({
          name_de: '', name_en: '', description_de: '', description_en: '',
          color: '#3182CE'
        });
      },
      onError: (error) => {
        toast({ title: t('common.error'), description: error.response?.data?.error || t('services.category_update_failed', {defaultValue: 'Failed to update category'}), status: 'error' });
      }
    }
  );

  const deleteCategory = useMutation(
    async (id) => {
      const response = await api.delete(`/api/services/categories/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['service-categories']);
        toast({ title: t('common.success'), description: t('services.category_deleted_success', {defaultValue: 'Category deleted successfully'}), status: 'success' });
      },
      onError: (error) => {
        toast({ title: t('common.error'), description: error.response?.data?.error || t('services.category_delete_failed', {defaultValue: 'Failed to delete category'}), status: 'error' });
      }
    }
  );

  // Category management functions
  const openCategoryModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryFormData({
        name_de: category.name_de || '',
        name_en: category.name_en || '',
        description_de: category.description_de || '',
        description_en: category.description_en || '',
        color: category.color || '#3182CE'
      });
    } else {
      setEditingCategory(null);
      setCategoryFormData({
        name_de: '', name_en: '', description_de: '', description_en: '',
        color: '#3182CE'
      });
    }
    setShowCategoryModal(true);
  };

  const handleCategorySubmit = () => {
    if (!categoryFormData.name_de) {
      toast({ title: t('common.error'), description: t('services.category_name_de_required', {defaultValue: 'Category name in German is required'}), status: 'error' });
      return;
    }
    if (editingCategory) {
      updateCategory.mutate({ id: editingCategory.id, data: categoryFormData });
    } else {
      createCategory.mutate(categoryFormData);
    }
  };

  const handleDeleteCategory = (id) => {
    if (window.confirm(t('services.category_confirm_delete', {defaultValue: 'Are you sure you want to delete this category?'}))) {
      deleteCategory.mutate(id);
    }
  };

  return (
    <Box p={6}>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">{t('settings.service_categories', {defaultValue: 'Service Categories'})}</Heading>
      </HStack>

      <VStack spacing={6} align="stretch">
        {/* Service Categories Management */}
        <Card bg={bgColor} border="1px" borderColor={borderColor}>
          <CardBody>
            <VStack align="start" spacing={4} w="100%">
              <HStack>
                <FiTool />
                <Heading size="md">{t('settings.service_categories', {defaultValue: 'Service Categories'})}</Heading>
              </HStack>
              <HStack w="100%">
                <Button 
                  leftIcon={<FiPlus />} 
                  colorScheme="blue" 
                  onClick={() => openCategoryModal()}
                >
                  {t('services.new_category', {defaultValue: 'New Category'})}
                </Button>
              </HStack>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>{t('common.name')}</Th>
                    <Th>{t('common.description')}</Th>
                    <Th>{t('common.color')}</Th>
                    <Th>{t('common.actions')}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {categoriesData?.data?.categories?.map((category) => (
                    <Tr key={category.id}>
                      <Td>
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="medium">{category.name_de || category.name_en}</Text>
                          {category.name_en && category.name_de && <Text fontSize="sm" color="gray.600">{category.name_en}</Text>}
                        </VStack>
                      </Td>
                      <Td>
                        <Text noOfLines={2}>{category.description_de || category.description_en || '-'}</Text>
                      </Td>
                      <Td>
                        <HStack>
                          <Box w="20px" h="20px" bg={category.color || '#3182CE'} borderRadius="md" border="1px" borderColor="gray.300" />
                          <Text fontSize="sm">{category.color || '#3182CE'}</Text>
                        </HStack>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <IconButton
                            icon={<FiEdit />}
                            size="sm"
                            variant="ghost"
                            onClick={() => openCategoryModal(category)}
                            aria-label={t('common.edit')}
                          />
                          <IconButton
                            icon={<FiTrash2 />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleDeleteCategory(category.id)}
                            aria-label={t('common.delete')}
                          />
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </VStack>
          </CardBody>
        </Card>
      </VStack>

      {/* Service Category Modal */}
      <AlertDialog isOpen={showCategoryModal} onClose={() => setShowCategoryModal(false)}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>
              {editingCategory ? t('services.category_edit_title', {defaultValue: 'Edit Category'}) : t('services.category_new_title', {defaultValue: 'Add New Category'})}
            </AlertDialogHeader>
            <AlertDialogBody>
              <VStack spacing={4}>
                <HStack spacing={4} w="100%">
                  <FormControl isRequired>
                    <FormLabel>{t('services.form.name_de')}</FormLabel>
                    <Input
                      value={categoryFormData.name_de}
                      onChange={(e) => setCategoryFormData(prev => ({ ...prev, name_de: e.target.value }))}
                      placeholder={t('services.form.category_name_de_placeholder', {defaultValue: 'Category name in German'})}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>{t('services.form.name_en')}</FormLabel>
                    <Input
                      value={categoryFormData.name_en}
                      onChange={(e) => setCategoryFormData(prev => ({ ...prev, name_en: e.target.value }))}
                      placeholder={t('services.form.category_name_en_placeholder', {defaultValue: 'Category name in English'})}
                    />
                  </FormControl>
                </HStack>
                
                <FormControl>
                  <FormLabel>{t('services.form.description_de', {defaultValue: 'Description (DE)'})}</FormLabel>
                  <Textarea
                    value={categoryFormData.description_de}
                    onChange={(e) => setCategoryFormData(prev => ({ ...prev, description_de: e.target.value }))}
                    placeholder={t('services.form.description_de_placeholder', {defaultValue: 'Category description in German'})}
                    rows={3}
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>{t('services.form.description_en', {defaultValue: 'Description (EN)'})}</FormLabel>
                  <Textarea
                    value={categoryFormData.description_en}
                    onChange={(e) => setCategoryFormData(prev => ({ ...prev, description_en: e.target.value }))}
                    placeholder={t('services.form.description_en_placeholder', {defaultValue: 'Category description in English'})}
                    rows={3}
                  />
                </FormControl>

                <HStack spacing={4} w="100%">
                  <FormControl>
                    <FormLabel>{t('common.color')}</FormLabel>
                    <Input
                      type="color"
                      value={categoryFormData.color}
                      onChange={(e) => setCategoryFormData(prev => ({ ...prev, color: e.target.value }))}
                    />
                  </FormControl>
                </HStack>
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button onClick={() => setShowCategoryModal(false)}>
                {t('common.cancel')}
              </Button>
              <Button 
                colorScheme="blue" 
                onClick={handleCategorySubmit} 
                ml={3}
                isLoading={createCategory.isLoading || updateCategory.isLoading}
              >
                {editingCategory ? t('common.update') : t('common.create')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default SettingsServiceCategories;
